// src/components/admin/AdminChartsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Container, Typography, Paper, Box, Grid, Card, CardContent,
  Skeleton, Alert, Button, Chip, Tabs, Tab
} from '@mui/material';
import {
  TrendingUp, People, Token,
  Assessment, Refresh
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area
} from 'recharts';
import { adminApi } from '../../api/adminApi';
import StatCard from '../common/StatCard';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  in: { opacity: 1, y: 0 },
  out: { opacity: 0, y: -20 }
};

const pageTransition = {
  type: "tween",
  ease: "anticipate",
  duration: 0.6
};

export default function AdminChartsPage() {
  const [activeTab, setActiveTab] = useState(0);
  const [stats, setStats] = useState(null);
  const [clientGrowthData, setClientGrowthData] = useState([]);
  const [tokenConsumptionData, setTokenConsumptionData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const normalizeTokenRows = (rows = []) =>
    rows.map((item) => ({
      ...item,
      tokens_in: Number(item.tokens_in || 0),
      tokens_out: Number(item.tokens_out || 0),
    }));

  const fetchChartData = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [statsData, growthData, tokenData] = await Promise.all([
        adminApi.getAdminStats(),
        adminApi.getClientGrowthChart(),
        adminApi.getTokenConsumptionChart()
      ]);

      setStats(statsData || null);
      setClientGrowthData(growthData || []);
      setTokenConsumptionData(normalizeTokenRows(tokenData || []));
    } catch (err) {
      console.error('Error fetching chart data:', err);
      setError(err.message || 'فشل في تحميل بيانات الرسوم البيانية');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('ar-SA', {
      month: 'short',
      day: 'numeric'
    });
  };

  const ChartCard = ({ title, children, loading = false }) => (
    <Card sx={{ height: '100%', borderRadius: 3 }}>
      <CardContent sx={{ p: 3, height: '100%' }}>
        <Typography variant="h6" fontWeight="600" gutterBottom>
          {title}
        </Typography>
        <Box sx={{ height: 350, mt: 2 }}>
          {loading ? (
            <Skeleton variant="rectangular" height="100%" sx={{ borderRadius: 2 }} />
          ) : (
            children
          )}
        </Box>
      </CardContent>
    </Card>
  );

  // Calculate Stats safely
  const totalClients = stats?.total_clients || 0;
  const newClients30d = clientGrowthData.reduce((sum, item) => sum + (item.count || 0), 0);

  const totalTokensIn = tokenConsumptionData.reduce((sum, item) => sum + (item.tokens_in || 0), 0);
  const totalTokensOut = tokenConsumptionData.reduce((sum, item) => sum + (item.tokens_out || 0), 0);
  const totalTokens = totalTokensIn + totalTokensOut;
  const avgDailyTokens = tokenConsumptionData.length > 0 ? Math.round(totalTokens / tokenConsumptionData.length) : 0;

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mb: 2 }} />
          <Skeleton variant="text" width="60%" height={32} />
        </Box>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={fetchChartData}>
              إعادة المحاولة
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <motion.div
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
      transition={pageTransition}
    >
      <Container maxWidth="lg" sx={{ pb: 8 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h4" component="h1" fontWeight="700">
              الرسوم البيانية والإحصائيات
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchChartData}
            >
              تحديث البيانات
            </Button>
          </Box>
          <Typography variant="body1" color="text.secondary">
            تحليل شامل لأداء النظام ونمو العملاء
          </Typography>
        </Box>

        {/* Quick Stats */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="إجمالي العملاء"
              value={totalClients}
              icon={<People color="primary" />}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="العملاء الجدد (30 يوم)"
              value={newClients30d}
              icon={<TrendingUp color="success" />}
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="التوكنز المستهلكة"
              value={stats?.total_tokens_consumed?.toLocaleString() || '0'}
              icon={<Token color="warning" />}
              color="warning"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="متوسط الاستهلاك اليومي"
              value={avgDailyTokens.toLocaleString()}
              icon={<Assessment color="info" />}
              color="info"
            />
          </Grid>
        </Grid>

        {/* Tabs */}
        <Paper sx={{ mb: 4, borderRadius: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="نمو العملاء" />
            <Tab label="استهلاك التوكنز" />
            <Tab label="تحليل شامل" />
          </Tabs>
        </Paper>

        {/* Tab Content */}
        {activeTab === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} lg={8}>
              <ChartCard title="نمو العملاء خلال 30 يوم">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={clientGrowthData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatDate}
                      reverse={true} // RTL handled visually usually, but dates order matters
                    />
                    <YAxis />
                    <Tooltip
                      labelFormatter={(label) => formatDate(label)}
                      formatter={(value) => [`${value} عميل`, 'العدد']}
                    />
                    <Legend />
                    <Bar name="عملاء جدد" dataKey="count" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </Grid>
            <Grid item xs={12} lg={4}>
              <ChartCard title="أحدث الإضافات">
                <Box sx={{ height: '100%', overflow: 'auto', pr: 1 }}>
                  {clientGrowthData.slice(-10).reverse().map((item, index) => (
                    <Box key={index} sx={{
                      p: 2,
                      mb: 1,
                      bgcolor: 'grey.50',
                      borderRadius: 2,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <Box>
                        <Typography variant="body2" fontWeight="600">
                          {formatDate(item.date)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.count} عميل جديد
                        </Typography>
                      </Box>
                      <Chip
                        label={item.count}
                        color="primary"
                        size="small"
                      />
                    </Box>
                  ))}
                </Box>
              </ChartCard>
            </Grid>
          </Grid>
        )}

        {activeTab === 1 && (
          <Grid container spacing={3}>
            <Grid item xs={12} lg={8}>
              <ChartCard title="استهلاك التوكنز خلال 30 يوم">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={tokenConsumptionData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatDate}
                    />
                    <YAxis />
                    <Tooltip
                      labelFormatter={(label) => formatDate(label)}
                    />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="tokens_out"
                      name="Mخرجات (Completion)"
                      stackId="1"
                      stroke="#22c55e"
                      fill="#22c55e"
                    />
                    <Area
                      type="monotone"
                      dataKey="tokens_in"
                      name="مدخلات (Prompt)"
                      stackId="1"
                      stroke="#0ea5e9"
                      fill="#0ea5e9"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartCard>
            </Grid>
            <Grid item xs={12} lg={4}>
              <ChartCard title="إحصائيات التوكنز">
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                      توكنز المدخلات (Prompts)
                    </Typography>
                    <Typography variant="h4" fontWeight="700" color="primary.main">
                      {totalTokensIn.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                      توكنز المخرجات (Completions)
                    </Typography>
                    <Typography variant="h4" fontWeight="700" color="success.main">
                      {totalTokensOut.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                      الإجمالي الكلي
                    </Typography>
                    <Typography variant="h4" fontWeight="700" color="warning.main">
                      {totalTokens.toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </ChartCard>
            </Grid>
          </Grid>
        )}

        {activeTab === 2 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <ChartCard title="تحليل شامل للنظام">
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Assessment sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      قريباً..
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      نعمل على إضافة تحليلات متقدمة باستخدام الذكاء الاصطناعي لفهم سلوك العملاء بشكل أفضل.
                    </Typography>
                  </Box>
                </Box>
              </ChartCard>
            </Grid>
          </Grid>
        )}
      </Container>
    </motion.div>
  );
}
